/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { Diagnostics } from '@tivocorp/tivo-webapp-loggers';
import {
    ActionKernelType,
    DeleteBookmarkActionKernel,
    DeleteRecordingActionKernel,
} from 'client-code/actions';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../config';
import { NdvrInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation';
import { TOverlayOptions } from '../../../core/types';
import { UIOrchestrator, UIOrchestratorState } from '../../../core/uiorchestrator';
import { DateTime } from '../../../core/utils';
import { WhisperTypes } from '../../../views/Whisper/WhisperTypes';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { showWhisper } from '../../whisper/whisper.reducer';

const GLOBAL_MOUSE_TRAP = 'deleteBookmarkOverlay';

// eslint-disable-next-line no-shadow
enum DeleteBookmarkOverlayActions {
    CANCEL,
    DELETE_BOOKMARK,
    DELETE_RECORDING,
}

const displayError = (_error) => (dispatch: Function) => {
    const whisperMessage = Locale.getString(LocaleKeys.FAILURE_WHISPER_MESSAGE);
    dispatch(
        showWhisper({
            message: whisperMessage,
            iconType: WhisperTypes.NO_ICON,
        }),
    );
    const errorMessage = _error instanceof Error ? _error.message : JSON.stringify(_error);
    Log.e('ProgramScreen', errorMessage);
    Diagnostics.error('ProgramScreen', errorMessage);
};

const getDeleteOverlaySubtitle = (action: any): string => {
    const { title, episodeNumber, seasonNumber, episodeTitle, startTime, movieYear } =
        action.kernel;
    let subtitle: string = title;

    if (episodeTitle) {
        const episodeTitleText = episodeTitle ? `${LEFT_QUOTE}${episodeTitle}${RIGHT_QUOTE}` : '';
        const episodeText: string = episodeNumber && episodeNumber > 0 ? `E${episodeNumber}` : '';
        const seasonText: string = seasonNumber && seasonNumber > 0 ? `S${seasonNumber} ` : '';
        subtitle += (seasonText + episodeText).length ? ` - ${seasonText}${episodeText} ` : '';
        subtitle =
            !subtitle.length && startTime ? new DateTime(startTime).strftime('%M/%D/%y') : subtitle;

        subtitle =
            subtitle.length && episodeTitleText?.length
                ? `${subtitle} ${episodeTitleText}`
                : subtitle;
    } else if (movieYear) {
        subtitle = `${subtitle} (${movieYear})`;
    }

    return subtitle;
};

const deleteBookmark = (contentId: string) => (dispatch: Function) => {
    new NdvrInteractor()
        .getRequestModelToDeleteBookmarkSchedule(contentId)
        .send()
        .then(() => {
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.BOOKMARK_DELETED);
        })
        .catch((error) => {
            dispatch(displayError(error));
        });
};

const deleteRecording = (recordingId: string) => (dispatch: Function) => {
    new NdvrInteractor()
        .getRequestModelToDeleteRecordingSchedule(recordingId)
        .send()
        .then(() => {
            UIOrchestrator.stateChangeListener.notify(UIOrchestratorState.RECORDING_DELETED);
        })
        .catch((error) => {
            dispatch(displayError(error));
        });
};

export const showDeleteOverlay = (action: any) => (dispatch: Function) => {
    const typeOfDeleteOverlay = action.kernel.actionKernelType;
    const overlayOptions: TOverlayOptions[] = [
        {
            text: Locale.getString(LocaleKeys.DELETE_OVERLAY_OPTION_DELETE_NOW),
            value: {
                type:
                    typeOfDeleteOverlay === ActionKernelType.DeleteRecordingActionKernel
                        ? DeleteBookmarkOverlayActions.DELETE_RECORDING
                        : DeleteBookmarkOverlayActions.DELETE_BOOKMARK,
                kernel: action.kernel,
            },
        },
        {
            text: Locale.getString(
                typeOfDeleteOverlay === ActionKernelType.DeleteRecordingActionKernel
                    ? LocaleKeys.DELETE_OVERLAY_OPTION_RECORDING_CANCEL
                    : LocaleKeys.DELETE_OVERLAY_OPTION_BOOKMARK_CANCEL,
            ),
            value: { type: DeleteBookmarkOverlayActions.CANCEL },
        },
    ];
    const overlayProps: IOverlayDialog = {
        hasDimmer: true,
        hasSeperator: false,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: overlayOptions,
        title:
            typeOfDeleteOverlay === ActionKernelType.DeleteRecordingActionKernel
                ? Locale.getString(LocaleKeys.DELETE_OVERLAY_RECORDING_TITLE)
                : Locale.getString(LocaleKeys.DELETE_OVERLAY_BOOKMARK_TITLE),
        subtitle: getDeleteOverlaySubtitle(action) || action.kernel.title,
    };
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.DELETE_OVERLAY_TASKS));
    dispatch(showOverlayDialog(overlayProps));
};

export const onMouseClickDeleteRecording = () => (dispatch: Function, getState: Function) => {
    const { value } = getState().inputFactory;

    if (value === GLOBAL_MOUSE_TRAP) {
        dispatch(hideOverlayDialog());
        return;
    }
    dispatch(onKeyOkDeleteOverlay());
};

export const onKeyOkDeleteOverlay = () => (dispatch: Function, getState: Function) => {
    const { overlayDialog, overlayHighlight } = getState();
    const { verticalPosition } = overlayHighlight;
    const { uiProperties } = overlayDialog;
    const { options } = uiProperties;
    const selectedOption = options[verticalPosition];
    dispatch(hideOverlayDialog());
    switch (selectedOption.value.type) {
        case DeleteBookmarkOverlayActions.DELETE_BOOKMARK:
            dispatch(
                deleteBookmark(
                    (selectedOption.value.kernel as DeleteBookmarkActionKernel).contentId,
                ),
            );
            break;
        case DeleteBookmarkOverlayActions.DELETE_RECORDING:
            dispatch(
                deleteRecording(
                    (selectedOption.value.kernel as DeleteRecordingActionKernel).recordingId,
                ),
            );
            break;
        case DeleteBookmarkOverlayActions.CANCEL:
            dispatch(hideOverlayDialog());
            break;
        default:
    }
};
